/**
 * AgentNotifyPicker - multi-select picker for the agents to email when an
 * Internal Note is saved (Greg's task 4, FreshDesk parity).
 *
 * The note's "To" acts like a public reply's "To" but may ONLY name
 * agents - the backend rejects non-agent addresses with a 422. Selecting
 * agents here does NOT change the ticket assignee; it just emails those
 * agents the note content.
 *
 * Built on the canonical searchable-picker shape (frontend/CLAUDE.md §9):
 * a disclosure button + an absolutely-positioned panel with a search
 * input and a result list. Outside-click and Escape close it;
 * ArrowUp/ArrowDown/Enter walk and pick. The staff directory is small,
 * so filtering is client-side over ``useAgents`` (no server search).
 */
import { useEffect, useId, useMemo, useRef, useState } from "react";

import { useAgents } from "@/hooks/useAgents";
import type { AgentDirectoryEntry } from "@/types/api";

export interface AgentNotifyPickerProps {
  /** Lowercased agent emails currently selected. */
  selectedEmails: string[];
  onChange: (emails: string[]) => void;
  /** Disable the whole control (e.g. while the note is posting). */
  disabled?: boolean;
}

export function AgentNotifyPicker({
  selectedEmails,
  onChange,
  disabled,
}: AgentNotifyPickerProps): JSX.Element {
  const autoId = useId();
  const listId = `${autoId}-list`;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(-1);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const agents = useAgents();
  // Guard against a non-array payload (a slow / errored fetch, or a test
  // mock that returns something else) so the iteration below never throws.
  const directory = useMemo<AgentDirectoryEntry[]>(
    () => (Array.isArray(agents.data) ? agents.data : []),
    [agents.data],
  );

  // Map email -> directory entry so a selected chip can show the name.
  const byEmail = useMemo(() => {
    const m = new Map<string, AgentDirectoryEntry>();
    for (const a of directory) m.set(a.email.toLowerCase(), a);
    return m;
  }, [directory]);

  const selectedSet = useMemo(
    () => new Set(selectedEmails.map((e) => e.toLowerCase())),
    [selectedEmails],
  );

  // Candidates: agents not already selected, filtered by the query
  // (matches name OR email). Client-side - the directory is small.
  const candidates = useMemo<AgentDirectoryEntry[]>(() => {
    const q = query.trim().toLowerCase();
    return directory.filter((a) => {
      if (selectedSet.has(a.email.toLowerCase())) return false;
      if (!q) return true;
      return (
        a.full_name.toLowerCase().includes(q) ||
        a.email.toLowerCase().includes(q)
      );
    });
  }, [directory, selectedSet, query]);

  useEffect(() => {
    setActiveIdx(candidates.length > 0 ? 0 : -1);
  }, [candidates]);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => inputRef.current?.focus());
    } else {
      setQuery("");
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(event: MouseEvent) {
      const target = event.target as Node | null;
      if (panelRef.current?.contains(target ?? null)) return;
      if (buttonRef.current?.contains(target ?? null)) return;
      setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
        buttonRef.current?.focus();
      }
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  function add(entry: AgentDirectoryEntry) {
    const email = entry.email.toLowerCase();
    if (selectedSet.has(email)) return;
    onChange([...selectedEmails, email]);
    // Keep the panel open for multi-add; clear the query so the next
    // pick starts from the full remaining list.
    setQuery("");
    requestAnimationFrame(() => inputRef.current?.focus());
  }

  function remove(email: string) {
    onChange(selectedEmails.filter((e) => e.toLowerCase() !== email.toLowerCase()));
  }

  function onInputKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIdx((idx) =>
        candidates.length === 0 ? -1 : (idx + 1) % candidates.length,
      );
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIdx((idx) =>
        candidates.length === 0
          ? -1
          : (idx - 1 + candidates.length) % candidates.length,
      );
    } else if (event.key === "Enter") {
      event.preventDefault();
      const row = candidates[activeIdx];
      if (row) add(row);
    }
  }

  function labelFor(email: string): string {
    return byEmail.get(email.toLowerCase())?.full_name || email;
  }

  return (
    <div
      className="rounded-lg border border-amber-200 bg-white px-3 py-2"
      data-testid="agent-notify-picker"
    >
      <div className="flex items-start gap-2 text-sm">
        <span className="mt-1 w-16 shrink-0 text-xs font-medium uppercase tracking-wide text-neutral-500">
          To
        </span>
        <div className="relative flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
          {selectedEmails.map((email) => (
            <span
              key={email}
              data-testid="agent-notify-chip"
              title={email}
              className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-900"
            >
              <span className="max-w-[14rem] truncate">{labelFor(email)}</span>
              <button
                type="button"
                aria-label={`Remove ${labelFor(email)}`}
                disabled={disabled}
                onClick={() => remove(email)}
                className="ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full text-sm leading-none text-amber-700 hover:bg-amber-200 hover:text-red-600 disabled:opacity-50"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </span>
          ))}
          <button
            ref={buttonRef}
            type="button"
            role="combobox"
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={listId}
            disabled={disabled}
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center gap-1 rounded-full border border-dashed border-neutral-300 px-2 py-0.5 text-xs text-neutral-600 hover:border-neutral-400 hover:text-neutral-800 disabled:opacity-50"
            data-testid="agent-notify-add"
          >
            <i className="fa-duotone fa-thin fa-plus" aria-hidden="true" />
            {selectedEmails.length === 0 ? "Notify an agent" : "Add agent"}
          </button>

          {open && (
            <div
              ref={panelRef}
              className="absolute left-0 top-full z-20 mt-1 w-72 overflow-hidden rounded-md border border-neutral-200 bg-white shadow-lg"
              data-testid="agent-notify-panel"
            >
              <div className="border-b border-neutral-100 p-2">
                <input
                  ref={inputRef}
                  type="search"
                  placeholder="Search agents by name or email..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onInputKeyDown}
                  aria-controls={listId}
                  aria-autocomplete="list"
                  className="block w-full rounded border border-neutral-300 px-2 py-1 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  data-testid="agent-notify-input"
                />
              </div>
              <ul
                id={listId}
                role="listbox"
                className="max-h-56 overflow-y-auto py-1"
              >
                {agents.isLoading && (
                  <li className="px-3 py-2 text-xs text-neutral-500">
                    Loading agents...
                  </li>
                )}
                {!agents.isLoading && candidates.length === 0 && (
                  <li className="px-3 py-2 text-xs text-neutral-500">
                    No matching agents.
                  </li>
                )}
                {candidates.map((row, idx) => (
                  <li
                    key={row.id}
                    role="option"
                    aria-selected={idx === activeIdx}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      add(row);
                    }}
                    onMouseEnter={() => setActiveIdx(idx)}
                    className={`cursor-pointer px-3 py-2 text-sm ${
                      idx === activeIdx
                        ? "bg-primary-50 text-primary-900"
                        : "text-neutral-800 hover:bg-neutral-50"
                    }`}
                    data-testid="agent-notify-option"
                  >
                    <div className="font-medium">
                      {row.full_name || "(no name)"}
                    </div>
                    <div className="text-xs text-neutral-500">{row.email}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
      <p className="mt-1 pl-[4.5rem] text-xs text-neutral-400">
        These agents get an email with the note. The assignee does not change.
      </p>
    </div>
  );
}

export default AgentNotifyPicker;
